/**
 * Number, money and date formatting.
 *
 * Currency is an explicit parameter here. In the prototype `money()` read a
 * module-global `state.currency`, which made every formatter implicitly
 * stateful and impossible to unit test. Components get the session currency
 * from `useCurrency()` and pass it in.
 */

export const CURRENCIES = ['EUR', 'GBP', 'USD'] as const;
export type Currency = (typeof CURRENCIES)[number];

const SYMBOLS: Record<Currency, string> = {
  EUR: '€',
  GBP: '£',
  USD: '$',
};

export const currencySymbol = (currency: Currency): string => SYMBOLS[currency];

/** Locale-formatted number with a fixed number of decimals. */
export const nf = (value: number, decimals = 0): string =>
  value.toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

export const pct = (value: number, decimals = 0): string => `${nf(value, decimals)}%`;

/** Signed percentage, used wherever a bonus/malus is shown. */
export const signedPct = (value: number, decimals = 0): string =>
  `${value >= 0 ? '+' : ''}${nf(value, decimals)}%`;

/**
 * Compact money: €1.2B / €4.5M / €120K / €480.
 *
 * The 8-figure threshold drops the decimal (€120M not €120.4M) because at that
 * magnitude the decimal is noise, and the exact figure is always available in
 * the table beside it.
 */
export function money(value: number, currency: Currency): string {
  const sym = SYMBOLS[currency];
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${sym}${nf(value / 1e9, 2)}B`;
  if (abs >= 1e6) return `${sym}${nf(value / 1e6, abs >= 1e8 ? 0 : 1)}M`;
  if (abs >= 1e3) return `${sym}${nf(value / 1e3, 0)}K`;
  return `${sym}${nf(value, 0)}`;
}

/** Full-precision money, for tables and totals that must reconcile. */
export const moneyExact = (value: number, currency: Currency): string =>
  `${SYMBOLS[currency]}${nf(value, 0)}`;

/** e.g. "24 Aug 2026" */
export const todayStr = (now: Date = new Date()): string =>
  now.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** e.g. "2026-08-24" — the storage form. */
export const isoDate = (now: Date = new Date()): string => now.toISOString().slice(0, 10);

/** Renders a stored ISO date for display. Invalid input is passed through. */
export function displayDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Pads a string to `width` for the fixed-width "working" panels. */
export const padEnd = (value: string | number, width: number): string =>
  String(value).padEnd(width);

export const padStart = (value: string | number, width: number): string =>
  String(value).padStart(width);

/** Truncates with an ellipsis. Used for long line items in chart labels. */
export const truncate = (value: string, max: number): string =>
  value.length > max ? `${value.slice(0, max - 1)}…` : value;
